import { Component, Inject, Optional } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { map, mergeMap } from 'rxjs/operators';
import { AircraftModel } from '@max/aircraft/models/aircraft.model';
import { Airport } from '@max/aircraft/models/airport.model';
import { AircraftLookupService } from '@max/aircraft/services/aircraft-lookup.service';
import { AircraftService } from '@max/aircraft/services/aircraft.service';
import { ILookup } from '@max/commercialpermitting/models/commercial-permit-lookup.interface';
import { County } from '@max/foundation/models/enums/county.enum';
import { BaseComponent } from '@max/shared/base-components/base.component';
import { ControlValidationMessagesService } from '@max/shared/validators/control-validation-messages.service';
import { MvdModalService } from '../../../../shared/services/modal/mvd-modal.service';
import { AddAircraftAddManufacturerModalComponent, AircraftAddManufacturerModalData } from '../add-aircraft-add-manufacturer-modal/add-aircraft-add-manufacturer-modal.component';

export class AircraftAddModalData {
    constructor(
        public faaNumber: string,
    ) {}
}

@Component({
    selector: 'add-aircraft-modal',
    templateUrl: './add-aircraft-modal.component.html'
})
export class AddAircraftModalComponent extends BaseComponent {
    public dataLoaded = false;
    public aircraftForm: UntypedFormGroup;
    public airports: Airport[];
    public aircraftMakes: ILookup[];
    public aircraftTypes: ILookup[];
    public aircraftMakeModels: ILookup[];
    public selectedAirport: Airport;
    public airportResults: string[];
    public makeResults: string[];
    public airportCounty: string;
    public airportCity: string;

    constructor(
        public dialogRef: MatDialogRef<AddAircraftModalComponent>,
        @Optional()
        @Inject(MAT_DIALOG_DATA)
        public data: AircraftAddModalData,
        private formBuilder: UntypedFormBuilder,
        private aircraftLookupService: AircraftLookupService,
        private aircraftService: AircraftService,
        private modalService: MvdModalService
    ) {
        super();
        this.buildAircraftForm(this.data);
    }

    public ngOnInit(): void {
        this.aircraftLookupService.getAirportLookups()
            .pipe(
                map((airports) => {
                    this.airports = airports.result;
                    return airports;
                }),
                mergeMap(() => this.aircraftLookupService.getAircraftLookups())
            )
            .subscribe((lookups) => {
                this.aircraftTypes = lookups.result.aircraftTypes;
                this.aircraftMakes = lookups.result.aircraftMakes;
                this.dataLoaded = true;
            });

    }

    public cancel = (): void => {
        this.dialogRef.close();
    };

    public save = (): void => {
        let aircraft = new AircraftModel();
        aircraft.aircraftModelId = parseInt(this.aircraftForm.get('model').value);
        aircraft.aircraftStatusTypeId = 1;
        aircraft.aircraftStorageLocationTypeId = 1;
        aircraft.faaNumber = this.aircraftForm.get('faaNumber').value;
        aircraft.airportId = parseInt(this.aircraftForm.get('airportId').value);
        aircraft.serialNumber = this.aircraftForm.get('serialNumber').value;
        aircraft.aircraftTypeId = parseInt(this.aircraftForm.get('aircraftTypeId').value);
        aircraft.modelYear = parseInt(this.aircraftForm.get('year').value);
        this.aircraftService.saveAircraft(aircraft)
            .subscribe(
                r => this.dialogRef.close(r.id),
                e => this.handleValidationResult(e));
    };

    private buildAircraftForm = (data?: AircraftAddModalData): void => {
        this.aircraftForm = this.formBuilder.group({
            faaNumber: [null, [Validators.maxLength(5), Validators.required]],
            serialNumber: [null, [Validators.required, Validators.maxLength(20)]],
            aircraftTypeId: [0, [Validators.required, ControlValidationMessagesService.requireSelectedItem]],
            year: [null, [Validators.required, ControlValidationMessagesService.maxAircraftModelYear]],
            manufacturer: [null, [Validators.required]],
            aircraftMakeId: [null, [Validators.required, ControlValidationMessagesService.requireSelectedItem]],
            model: [0, [Validators.required, ControlValidationMessagesService.requireSelectedItem]],
            airport: [null, [Validators.required]],
            airportId: [null, [Validators.required, ControlValidationMessagesService.requireSelectedItem]]
        });

        if (null != data.faaNumber) {
            this.aircraftForm.get('faaNumber').setValue(data.faaNumber);
        }
    };

    public selectAirport = (search: string): void => {
        let selectedAirport = this.airports.find(x => x.displayValue.toLowerCase().includes(search.toLowerCase()));
        this.aircraftForm.get('airportId').setValue(selectedAirport.id);
        let county = new County().FromId(selectedAirport.countyId);
        this.airportCounty = county.displayValue;
        this.airportCity = selectedAirport.city;
        this.selectedAirport = selectedAirport;
    };

    public searchAirports = (event: {query: string}): void => {
        let filteredAirports = this.airports.filter(x => x.displayValue.toLowerCase().includes(event.query.toLowerCase()));
        this.airportResults = filteredAirports.map(x => x.name);
    };

    public selectMake = (search: string): void => {
        let selectedMake = this.aircraftMakes.find(x => x.text.toLowerCase() === search.toLowerCase());
        if (selectedMake) {
            this.aircraftForm.get('aircraftMakeId').setValue(selectedMake.value);
            this.aircraftLookupService.getAircraftModelByMakeLookups(parseInt(selectedMake.value))
                .subscribe((models) => {
                    this.aircraftForm.get('aircraftMakeId').setValue(selectedMake.value);
                    this.aircraftMakeModels = models.result.aircraftModelsByMake;
                });
        }
    };

    public searchMakes = (event: {query: string}): void => {
        let filteredMakes = this.aircraftMakes.filter(x => x.text.toLowerCase().includes(event.query.toLowerCase()));
        this.makeResults = filteredMakes.map(x => x.text);
    };

    public addNewManufacturer = (): void => {
        const data = new AircraftAddManufacturerModalData();
        this.modalService.open(AddAircraftAddManufacturerModalComponent, data).afterClosed()
            .subscribe((result) => {
                if (result) {
                    this.selectNewModel(result);
                }
            });
    };

    public addNewModel = (): void => {
        const data = new AircraftAddManufacturerModalData();
        data.makeId = this.aircraftForm.get('aircraftMakeId').value;
        this.modalService.open(AddAircraftAddManufacturerModalComponent, data).afterClosed()
            .subscribe((result) => {
                if (result) {
                    this.selectNewModel(result);
                }
            });
    };

    public selectNewModel = (result): void => {
        this.aircraftLookupService.getAircraftLookups().subscribe(l => {
            this.aircraftMakes = l.result.aircraftMakes;
            this.aircraftForm.get('aircraftMakeId').setValue(result.aircraftMakeId);
            this.aircraftForm.get('manufacturer').setValue(result.aircraftMakeDisplayValue);
            this.aircraftLookupService.getAircraftModelByMakeLookups(parseInt(result.aircraftMakeId))
                .subscribe((models) => {
                    this.aircraftMakeModels = models.result.aircraftModelsByMake;
                    this.aircraftForm.get('model').setValue(result.aircraftModelId);
                });
        });
    };
}
